import { FeatureNotification } from 'district-ui-client/helpers/feature-notification'
import { FeatureNotificationId } from 'district-ui-client/services/feature-notification'
import { Lozenge } from 'district-ui-client/components/lozenge'
import { SelectBox } from 'district-ui-client/components/select-box'
import { service } from '@ember/service'
import { t } from 'ember-intl'
import Component from '@glimmer/component'
import type { IntlService } from 'ember-intl'
import type { Product } from 'district-ui-client/domain/product'
import type { ReportPath } from 'district-ui-client/services/report-registry'
import type { SelectOption } from '@blakeelearning/ember-select'
import type ReportRegistryService from 'district-ui-client/services/report-registry'

interface ReportPickerSignature {
  Args: {
    product: Product
    selected: string
    onReportChange: (value: SelectOption) => void
  }
}

export class ReportPicker extends Component<ReportPickerSignature> {
  @service intl!: IntlService

  @service reportRegistry!: ReportRegistryService

  get reports(): ReportPath[] {
    const { product } = this.args
    return this.reportRegistry.reportPathsForProduct(product)
  }

  get reportsAsOptions(): SelectOption[] {
    return this.reports.map((reportPath) => ({
      value: reportPath,
      label: this.reportRegistry.reportName(reportPath).replace('<br />', ' '),
    }))
  }

  get isDisabled() {
    // disable the picker if there is only one option and it is the selected option
    // this is to prevent the user from pointlessly clicking the picker
    return this.reports.length === 1 && String(this.reports[0]) === this.args.selected
  }

  <template>
    <label class="flex flex-col gap-y-1" data-test-report-picker>
      <div class="flex items-center gap-2">
        <span class="text-sm font-medium text-neutral-250">{{t "report"}}</span>
        {{! show "NEW" lozenge if there's a new report for the selected product }}
        {{#if (FeatureNotification FeatureNotificationId.NewReportForProduct reportProduct=@product)}}
          <Lozenge>{{t "new"}}</Lozenge>
        {{/if}}
      </div>
      <SelectBox
        @style="subtle"
        @disabled={{this.isDisabled}}
        @options={{this.reportsAsOptions}}
        @value={{@selected}}
        @onSelect={{@onReportChange}}
      >
        <:option as |option|>
          <div class="flex items-center justify-between space-x-2">
            <span class="flex-grow">{{option.label}}</span>
            {{! show "NEW" lozenge if option is a new report }}
            {{#if (FeatureNotification FeatureNotificationId.NewReportForPath reportPath=option.value)}}
              <Lozenge class="shrink-0 grow-0 px-2">{{t "new"}}</Lozenge>
            {{/if}}
          </div>
        </:option>
      </SelectBox>
    </label>
  </template>
}
